import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForceLightTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { ChevronRight, ChevronLeft, User, MapPin, Trophy, Users, Target, Heart } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileSetup = () => {
  useForceLightTheme();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    display_name: '', birth_month: '', birth_day: '', birth_year: '', gender: '', bio: '', profile_photo: '',
    location: '', travel_distance: 25,
    ghin_number: '', ghin_status: 'unknown', handicap: '', handicap_official: false,
    skill_level: '', is_private_club_member: false, private_club_name: '',
    playing_pace: '', walking_or_riding: 'either',
    plays_music: false, drinks_on_course: '', smokes_on_course: '', follows_golf: null, plays_for_money: null,
    preferred_group_sizes: [], availability: [], preferred_tee_times: [],
    looking_for: 'playing_partner', group_size: 1, gender_preference: 'any',
    min_age: 18, max_age: 75, min_handicap: 0, max_handicap: 36, must_have_handicap: false,
    pref_pace_min: 'fast', pref_pace_max: 'slow',
    pref_music: 'any', pref_drinking: 'any', pref_smoking: 'any', pref_walking: 'any', pref_gambling: 'any',
  });

  const [geocoding, setGeocoding] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const cityTimer = useRef(null);

  const onCityChange = (val) => {
    h('city', val);
    h('state', '');
    if (cityTimer.current) clearTimeout(cityTimer.current);
    if (!val || val.length < 3) { setCitySuggestions([]); return; }
    cityTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/golf/city-autocomplete`, { params: { q: val } });
        setCitySuggestions(res.data || []);
      } catch { setCitySuggestions([]); }
    }, 350);
  };

  const selectCity = (s) => {
    setCitySuggestions([]);
    setFormData(prev => ({ ...prev, city: s.city, state: s.state, location: `${s.city}, ${s.state}` }));
    geocodeCity(s.city);
  };

  const totalSteps = 6;
  const steps = [
    { number: 1, title: 'About You', icon: User },
    { number: 2, title: 'Location', icon: MapPin },
    { number: 3, title: 'Golf Info', icon: Trophy },
    { number: 4, title: 'On Course', icon: Heart },
    { number: 5, title: 'Looking For', icon: Users },
    { number: 6, title: 'Match Prefs', icon: Target },
  ];

  const h = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleList = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const canProceed = () => {
    if (step === 1) return formData.display_name && formData.birth_year && formData.birth_month && formData.gender;
    if (step === 2) return formData.city && formData.state;
    return true;
  };

  // Auto-fill state from city via geocoding
  const geocodeCity = async (city) => {
    if (!city || city.length < 2) return;
    setGeocoding(true);
    try {
      const res = await axios.get(`${API}/golf/geocode-city`, { params: { city } });
      if (res.data.state) {
        h('state', res.data.state);
        h('location', `${city}, ${res.data.state}`);
      }
      if (res.data.latitude) h('latitude', res.data.latitude);
      if (res.data.longitude) h('longitude', res.data.longitude);
    } catch { /* silent */ }
    finally { setGeocoding(false); }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build birthday from separate fields
      const birthday = formData.birth_year && formData.birth_month && formData.birth_day
        ? new Date(parseInt(formData.birth_year), parseInt(formData.birth_month) - 1, parseInt(formData.birth_day || 1)).toISOString()
        : null;
      const age = birthday
        ? Math.floor((Date.now() - new Date(birthday).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
        : parseInt(formData.age) || null;
      const location = formData.city && formData.state ? `${formData.city}, ${formData.state}` : formData.location;
      const submitData = {
        ...formData,
        age,
        birthday,
        location,
        handicap: formData.handicap ? parseFloat(formData.handicap) : null,
      };
      delete submitData.birth_month;
      delete submitData.birth_day;
      delete submitData.birth_year;
      delete submitData.city;
      const response = await axios.put(`${API}/profile`, submitData);
      updateUser(response.data);
      toast.success('Profile complete! Start discovering golfers.');
      navigate('/social');
    } catch { toast.error('Failed to save profile. Please try again.'); } finally { setLoading(false); }
  };

  const inputCls = "h-12 rounded-xl border bg-white/80 focus:bg-white transition-colors";
  const inputStyle = { borderColor: 'var(--fairway-sand)', color: '#000', WebkitTextFillColor: '#000' };

  const ChipSelect = ({ options, selected, onToggle, multi = true }) => (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const active = multi ? selected.includes(opt.value) : selected === opt.value;
        return (
          <button key={opt.value} type="button" onClick={() => onToggle(opt.value)}
            className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all border-2"
            style={{
              borderColor: active ? 'var(--club-green)' : 'var(--fairway-sand)',
              background: active ? 'rgba(15,59,46,0.06)' : 'transparent',
              color: active ? 'var(--club-green)' : 'var(--warm-gray)',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1: return (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Display Name *</Label>
            <Input placeholder="How should others see you?" value={formData.display_name} onChange={e => h('display_name', e.target.value)} className={inputCls} style={inputStyle} data-testid="display-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Birthday *</Label>
            <div className="grid grid-cols-3 gap-2">
              <Select value={formData.birth_month || ''} onValueChange={v => h('birth_month', v)}>
                <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="birth-month-select"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={formData.birth_day || ''} onValueChange={v => h('birth_day', v)}>
                <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="birth-day-select"><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent>
                  {Array.from({length: 31}, (_, i) => (
                    <SelectItem key={i+1} value={String(i + 1)}>{i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={formData.birth_year || ''} onValueChange={v => h('birth_year', v)}>
                <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="birth-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {Array.from({length: 70}, (_, i) => {
                    const yr = new Date().getFullYear() - 16 - i;
                    return <SelectItem key={yr} value={String(yr)}>{yr}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Sex *</Label>
            <Select value={formData.gender} onValueChange={v => h('gender', v)}>
              <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Bio</Label>
            <Textarea placeholder="Tell others about yourself and your golf game..." value={formData.bio} onChange={e => h('bio', e.target.value)} className="min-h-20 rounded-xl border bg-white/80" style={inputStyle} data-testid="bio-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Profile Photo</Label>
            <div className="flex items-center gap-3">
              <label
                className="flex-1 flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed cursor-pointer transition-colors hover:border-solid"
                style={{ borderColor: 'var(--fairway-sand)', color: 'var(--warm-gray)', background: 'var(--surface-secondary)' }}
                data-testid="photo-upload-btn"
              >
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
                    const fd = new FormData();
                    fd.append('file', file);
                    try {
                      toast.info('Uploading photo...');
                      const res = await axios.post(`${API}/photos/upload`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        timeout: 30000,
                      });
                      h('profile_photo', res.data.photo_url);
                      toast.success('Photo uploaded!');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Upload failed. Try a smaller image.');
                    }
                  }}
                />
                {formData.profile_photo ? (
                  <span className="text-sm font-medium" style={{ color: 'var(--club-green)' }}>Photo added — tap to change</span>
                ) : (
                  <span className="text-sm">Tap to add a photo</span>
                )}
              </label>
            </div>
            <p className="text-[10px]" style={{ color: 'var(--warm-gray)' }}>Supports JPEG, PNG, WebP, and HEIC. Max 10MB.</p>
          </div>
        </div>
      );

      case 2: return (
        <div className="space-y-5">
          <div className="space-y-1.5 relative">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>City *</Label>
            <Input placeholder="Start typing your city..." value={formData.city || ''} onChange={e => onCityChange(e.target.value)}
              onBlur={e => { setTimeout(() => setCitySuggestions([]), 200); if (!formData.state) geocodeCity(e.target.value); }}
              autoComplete="off"
              className={inputCls} style={inputStyle} data-testid="city-input" />
            {citySuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-xl border bg-white shadow-lg overflow-hidden" style={{ borderColor: 'var(--fairway-sand)' }} data-testid="city-suggestions">
                {citySuggestions.map((s, i) => (
                  <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); selectCity(s); }}
                    className="w-full px-3 py-2.5 text-left text-sm hover:bg-black/5 transition-colors"
                    style={{ color: '#08241C' }}
                    data-testid={`city-option-${i}`}>
                    {s.city}, {s.state}
                  </button>
                ))}
              </div>
            )}
            {geocoding && <p className="text-xs" style={{ color: 'var(--club-green)' }}>Detecting state...</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>State *</Label>
            <Select value={formData.state || ''} onValueChange={v => { h('state', v); h('location', `${formData.city || ''}, ${v}`); }}>
              <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent>
                {[
                  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
                  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
                  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
                ].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Travel Distance</Label>
              <span className="text-sm font-bold" style={{ color: 'var(--club-green)' }}>{formData.travel_distance} miles</span>
            </div>
            <Slider value={[formData.travel_distance]} onValueChange={([v]) => h('travel_distance', v)} max={100} min={5} step={5} data-testid="distance-slider" />
            <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>How far are you willing to travel to play?</p>
          </div>
          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Availability</Label>
            <ChipSelect
              options={[
                { value: 'weekday_mornings', label: 'Weekday AM' },
                { value: 'weekday_afternoons', label: 'Weekday PM' },
                { value: 'weekend_mornings', label: 'Weekend AM' },
                { value: 'weekend_afternoons', label: 'Weekend PM' },
              ]}
              selected={formData.availability}
              onToggle={v => toggleList('availability', v)}
            />
          </div>
        </div>
      );

      case 3: return (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Handicap Status</Label>
            <Select value={formData.ghin_status} onValueChange={v => h('ghin_status', v)}>
              <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="ghin-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="verified">I have an official GHIN</SelectItem>
                <SelectItem value="estimated">I know my estimated handicap</SelectItem>
                <SelectItem value="unknown">New to golf / I don't know</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.ghin_status === 'unknown' && (
            <div className="rounded-xl p-4" style={{ background: 'rgba(15,59,46,0.06)', border: '1px solid var(--fairway-sand)' }}>
              <p className="text-sm font-semibold mb-3" style={{ color: 'var(--deep-forest)' }}>What's a handicap?</p>
              <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--warm-gray)' }}>
                A handicap measures your skill level based on what you typically shoot. Here's a quick guide:
              </p>
              <div className="space-y-1.5 text-xs" style={{ color: 'var(--warm-gray)' }}>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--fairway-sand)' }}>
                  <span className="font-semibold" style={{ color: 'var(--deep-forest)' }}>Scratch (0)</span>
                  <span>Shoots around 72</span>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--fairway-sand)' }}>
                  <span className="font-semibold" style={{ color: 'var(--deep-forest)' }}>Single Digit (1-9)</span>
                  <span>Shoots 73-81</span>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--fairway-sand)' }}>
                  <span className="font-semibold" style={{ color: 'var(--deep-forest)' }}>Mid Handicap (10-18)</span>
                  <span>Shoots 82-90</span>
                </div>
                <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--fairway-sand)' }}>
                  <span className="font-semibold" style={{ color: 'var(--deep-forest)' }}>High Handicap (19-28)</span>
                  <span>Shoots 91-100</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="font-semibold" style={{ color: 'var(--deep-forest)' }}>Beginner (29+)</span>
                  <span>Shoots 100+</span>
                </div>
              </div>
              <p className="text-[10px] mt-3" style={{ color: 'var(--warm-gray)' }}>
                Based on a standard par 72 course (18 holes). Don't know yours? No problem — we'll calculate it as you play!
              </p>
            </div>
          )}
          {formData.ghin_status === 'verified' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>GHIN Number</Label>
              <Input placeholder="Enter your GHIN number" value={formData.ghin_number} onChange={e => h('ghin_number', e.target.value)} className={inputCls} style={inputStyle} data-testid="ghin-number-input" />
            </div>
          )}
          {(formData.ghin_status === 'verified' || formData.ghin_status === 'estimated') && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Handicap Index</Label>
                <Input type="number" step="0.1" placeholder="e.g., 12.4" value={formData.handicap} onChange={e => h('handicap', e.target.value)} className={inputCls} style={inputStyle} data-testid="handicap-input" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--fairway-sand)' }}>
                <div>
                  <Label className="text-sm font-semibold">Official Handicap?</Label>
                  <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>Is this a GHIN/official index?</p>
                </div>
                <Switch checked={formData.handicap_official} onCheckedChange={v => h('handicap_official', v)} data-testid="handicap-official-switch" />
              </div>
            </>
          )}
          {formData.ghin_status === 'unknown' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Skill Level</Label>
              <ChipSelect
                options={[
                  { value: 'beginner', label: 'Beginner' },
                  { value: 'recreational', label: 'Recreational' },
                  { value: 'competitive', label: 'Competitive' },
                  { value: 'scratch', label: 'Scratch+' },
                ]}
                selected={formData.skill_level}
                onToggle={v => h('skill_level', v)}
                multi={false}
              />
            </div>
          )}
          <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--fairway-sand)' }}>
            <div>
              <Label className="text-sm font-semibold">Private Club Member?</Label>
              <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>Can you host guests?</p>
            </div>
            <Switch checked={formData.is_private_club_member} onCheckedChange={v => h('is_private_club_member', v)} data-testid="private-club-switch" />
          </div>
          {formData.is_private_club_member && (
            <Input placeholder="Club name" value={formData.private_club_name} onChange={e => h('private_club_name', e.target.value)} className={inputCls} style={inputStyle} data-testid="club-name-input" />
          )}
        </div>
      );

      case 4: return (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Playing Pace</Label>
            <Select value={formData.playing_pace} onValueChange={v => h('playing_pace', v)}>
              <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="pace-select"><SelectValue placeholder="How do you play?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ready_golf">Ready Golf - I play when ready</SelectItem>
                <SelectItem value="quick">Quick to the Ball - Minimal delay</SelectItem>
                <SelectItem value="average">Average Pace - Standard rhythm</SelectItem>
                <SelectItem value="deliberate">Takes My Time - Reads every putt</SelectItem>
                <SelectItem value="very_deliberate">Very Deliberate - Full routine, every shot</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Walking or Riding?</Label>
            <ChipSelect
              options={[
                { value: 'walk', label: 'Walk' },
                { value: 'ride', label: 'Cart' },
                { value: 'either', label: 'Either' },
              ]}
              selected={formData.walking_or_riding}
              onToggle={v => h('walking_or_riding', v)}
              multi={false}
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--fairway-sand)' }}>
            <Label className="text-sm font-semibold">Listen to music on course?</Label>
            <Switch checked={formData.plays_music} onCheckedChange={v => h('plays_music', v)} data-testid="music-switch" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Do you drink on course? <span className="font-normal">(optional)</span></Label>
            <ChipSelect
              options={[{ value: 'yes', label: 'Yes' }, { value: 'sometimes', label: 'Sometimes' }, { value: 'no', label: 'No' }]}
              selected={formData.drinks_on_course}
              onToggle={v => h('drinks_on_course', v)}
              multi={false}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Do you smoke on course? <span className="font-normal">(optional)</span></Label>
            <ChipSelect
              options={[{ value: 'yes', label: 'Yes' }, { value: 'sometimes', label: 'Sometimes' }, { value: 'no', label: 'No' }]}
              selected={formData.smokes_on_course}
              onToggle={v => h('smokes_on_course', v)}
              multi={false}
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--fairway-sand)' }}>
            <Label className="text-sm font-semibold">Do you follow golf / watch tours?</Label>
            <Switch checked={formData.follows_golf === true} onCheckedChange={v => h('follows_golf', v)} data-testid="follows-golf-switch" />
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--fairway-sand)' }}>
            <Label className="text-sm font-semibold">Enjoy friendly betting?</Label>
            <Switch checked={formData.plays_for_money === true} onCheckedChange={v => h('plays_for_money', v)} data-testid="gambling-switch" />
          </div>
        </div>
      );

      case 5: return (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>I'm Looking For</Label>
            <Select value={formData.looking_for} onValueChange={v => h('looking_for', v)}>
              <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="looking-for-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="playing_partner">Playing Partner</SelectItem>
                <SelectItem value="group_fill">Group to Join</SelectItem>
                <SelectItem value="club_member">Club Members to Recruit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Preferred Group Size</Label>
            <ChipSelect
              options={[
                { value: 2, label: 'Twosome' },
                { value: 3, label: 'Threesome' },
                { value: 4, label: 'Foursome' },
              ]}
              selected={formData.preferred_group_sizes}
              onToggle={v => toggleList('preferred_group_sizes', v)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Gender Preference</Label>
            <Select value={formData.gender_preference} onValueChange={v => h('gender_preference', v)}>
              <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="gender-pref-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Preferred Tee Times</Label>
            <ChipSelect
              options={[
                { value: 'morning', label: 'Morning' },
                { value: 'afternoon', label: 'Afternoon' },
                { value: 'evening', label: 'Twilight' },
                { value: 'weekend', label: 'Weekends' },
              ]}
              selected={formData.preferred_tee_times}
              onToggle={v => toggleList('preferred_tee_times', v)}
            />
          </div>
        </div>
      );

      case 6: return (
        <div className="space-y-5">
          <p className="text-sm" style={{ color: 'var(--warm-gray)' }}>Set your preferences for who appears in your Discover feed.</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Partner Age Range</Label>
              <span className="text-sm font-bold" style={{ color: 'var(--club-green)' }}>{formData.min_age} - {formData.max_age}</span>
            </div>
            <div className="flex gap-4">
              <div className="flex-1"><span className="text-[10px]" style={{ color: 'var(--warm-gray)' }}>Min</span><Slider value={[formData.min_age]} onValueChange={([v]) => h('min_age', Math.min(v, formData.max_age))} max={80} min={18} step={1} data-testid="min-age-slider" /></div>
              <div className="flex-1"><span className="text-[10px]" style={{ color: 'var(--warm-gray)' }}>Max</span><Slider value={[formData.max_age]} onValueChange={([v]) => h('max_age', Math.max(v, formData.min_age))} max={80} min={18} step={1} data-testid="max-age-slider" /></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Partner Handicap Range</Label>
              <span className="text-sm font-bold" style={{ color: 'var(--club-green)' }}>{formData.min_handicap} - {formData.max_handicap}</span>
            </div>
            <p className="text-[10px] leading-relaxed" style={{ color: 'var(--warm-gray)' }}>
              Scratch (0) shoots ~72 · Single digit (1-9) shoots 73-81 · Mid (10-18) shoots 82-90 · High (19+) shoots 91+
            </p>
            <div className="flex gap-4">
              <div className="flex-1"><span className="text-[10px]" style={{ color: 'var(--warm-gray)' }}>Min</span><Slider value={[formData.min_handicap]} onValueChange={([v]) => h('min_handicap', Math.min(v, formData.max_handicap))} max={54} min={0} step={1} data-testid="min-handicap-slider" /></div>
              <div className="flex-1"><span className="text-[10px]" style={{ color: 'var(--warm-gray)' }}>Max</span><Slider value={[formData.max_handicap]} onValueChange={([v]) => h('max_handicap', Math.max(v, formData.min_handicap))} max={54} min={0} step={1} data-testid="max-handicap-slider" /></div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Partner Playing Pace</Label>
            <div className="grid grid-cols-2 gap-3">
              <Select value={formData.pref_pace_min} onValueChange={v => h('pref_pace_min', v)}>
                <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="pref-pace-min"><SelectValue placeholder="Min pace" /></SelectTrigger>
                <SelectContent>
                  {[{v:'ready_golf',l:'Ready Golf'},{v:'quick',l:'Quick'},{v:'average',l:'Average'},{v:'deliberate',l:'Deliberate'},{v:'very_deliberate',l:'Very Deliberate'}].map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={formData.pref_pace_max} onValueChange={v => h('pref_pace_max', v)}>
                <SelectTrigger className="h-12 rounded-xl" style={inputStyle} data-testid="pref-pace-max"><SelectValue placeholder="Max pace" /></SelectTrigger>
                <SelectContent>
                  {[{v:'ready_golf',l:'Ready Golf'},{v:'quick',l:'Quick'},{v:'average',l:'Average'},{v:'deliberate',l:'Deliberate'},{v:'very_deliberate',l:'Very Deliberate'}].map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* On-course behavior preferences */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Music on Course</Label>
            <ChipSelect options={[{value:'any',label:'No Preference'},{value:'yes',label:'Plays Music'},{value:'no',label:'No Music'}]} selected={formData.pref_music} onToggle={v => h('pref_music', v)} multi={false} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Drinking on Course</Label>
            <ChipSelect options={[{value:'any',label:'No Preference'},{value:'yes',label:'Drinks'},{value:'no',label:"Doesn't Drink"}]} selected={formData.pref_drinking} onToggle={v => h('pref_drinking', v)} multi={false} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Smoking on Course</Label>
            <ChipSelect options={[{value:'any',label:'No Preference'},{value:'yes',label:'Smokes'},{value:'no',label:"Doesn't Smoke"}]} selected={formData.pref_smoking} onToggle={v => h('pref_smoking', v)} multi={false} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Walking or Riding</Label>
            <ChipSelect options={[{value:'any',label:'No Preference'},{value:'walk',label:'Walkers'},{value:'ride',label:'Riders'}]} selected={formData.pref_walking} onToggle={v => h('pref_walking', v)} multi={false} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase" style={{ color: 'var(--warm-gray)' }}>Friendly Betting</Label>
            <ChipSelect options={[{value:'any',label:'No Preference'},{value:'yes',label:'Likes to Bet'},{value:'no',label:'No Betting'}]} selected={formData.pref_gambling} onToggle={v => h('pref_gambling', v)} multi={false} />
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--fairway-sand)' }}>
            <div>
              <Label className="text-sm font-semibold">Must Have Handicap?</Label>
              <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>Only show golfers with a handicap</p>
            </div>
            <Switch checked={formData.must_have_handicap} onCheckedChange={v => h('must_have_handicap', v)} data-testid="must-handicap-switch" />
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: 'var(--club-cream)' }} data-testid="profile-setup-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <img src="/logo-the-club.png" alt="The Club" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-bold" style={{ color: 'var(--deep-forest)' }}>Complete Your Profile</h1>
          <p className="text-sm mt-2" style={{ color: 'var(--warm-gray)' }}>Help us find your perfect golf partners</p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-between mb-8 px-2">
          {steps.map((s, i) => (
            <React.Fragment key={s.number}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${step >= s.number ? '' : ''}`}
                  style={{ background: step >= s.number ? 'var(--club-green)' : 'var(--fairway-sand)', color: step >= s.number ? '#fff' : 'var(--warm-gray)' }}>
                  <s.icon className="w-4 h-4" />
                </div>
                <span className="text-[9px] mt-1 font-semibold" style={{ color: step >= s.number ? 'var(--club-green)' : 'var(--warm-gray)' }}>{s.title}</span>
              </div>
              {i < steps.length - 1 && (
                <div className="flex-1 h-0.5 mx-1.5 rounded" style={{ background: step > s.number ? 'var(--club-green)' : 'var(--fairway-sand)' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="rounded-2xl border shadow-club-md" style={{ borderColor: 'var(--fairway-sand)' }}>
          <CardHeader>
            <CardTitle className="font-serif text-xl" style={{ color: 'var(--deep-forest)' }}>{steps[step - 1].title}</CardTitle>
            <CardDescription>Step {step} of {totalSteps}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStep()}
            <div className="flex items-center justify-between mt-8 pt-6 border-t" style={{ borderColor: 'var(--fairway-sand)' }}>
              <Button variant="ghost" onClick={() => setStep(s => s - 1)} disabled={step === 1} className="gap-2" data-testid="back-btn">
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
              {step < totalSteps ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={!canProceed()} className="gap-2 rounded-full px-6" style={{ background: 'var(--club-green)', color: '#fff' }} data-testid="next-btn">
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading} className="gap-2 rounded-full px-6 font-semibold" style={{ background: 'var(--gold)', color: 'var(--deep-forest)' }} data-testid="complete-btn">
                  {loading ? 'Saving...' : 'Complete Profile'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileSetup;
